import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Brain, Database, PenTool, HardHat, Leaf, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: 'Brain', label: 'Brain (AI)', icon: Brain },
  { value: 'Database', label: 'Database', icon: Database },
  { value: 'PenTool', label: 'Pen Tool (Design)', icon: PenTool },
  { value: 'HardHat', label: 'Hard Hat (Construction)', icon: HardHat },
  { value: 'Leaf', label: 'Leaf (Green)', icon: Leaf },
  { value: 'Building2', label: 'Building', icon: Building2 },
];

const ManageServices = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '',
    title_id: '',
    description_en: '',
    description_id: '',
    category: 'it',
    icon: 'Brain',
    features_en: '',
    features_id: '',
    is_active: true
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to fetch services' : 'Gagal mengambil layanan');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingService(null);
    setFormData({
      title_en: '',
      title_id: '',
      description_en: '',
      description_id: '',
      category: 'it',
      icon: 'Brain',
      features_en: '',
      features_id: '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      title_en: service.title_en,
      title_id: service.title_id,
      description_en: service.description_en,
      description_id: service.description_id,
      category: service.category,
      icon: service.icon,
      features_en: service.features_en.join(', '),
      features_id: service.features_id.join(', '),
      is_active: service.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const serviceData = {
      ...formData,
      features_en: formData.features_en.split(',').map(f => f.trim()).filter(f => f),
      features_id: formData.features_id.split(',').map(f => f.trim()).filter(f => f)
    };

    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, serviceData);
        toast.success(language === 'en' ? 'Service updated!' : 'Layanan diperbarui!');
      } else {
        await axios.post(`${API}/services`, serviceData);
        toast.success(language === 'en' ? 'Service created!' : 'Layanan dibuat!');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'en' ? 'Delete this service?' : 'Hapus layanan ini?')) return;
    
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success(language === 'en' ? 'Service deleted!' : 'Layanan dihapus!');
      fetchServices();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to delete' : 'Gagal menghapus');
    }
  };

  const getIcon = (iconName) => {
    const found = iconOptions.find(o => o.value === iconName);
    const Icon = found?.icon || Brain;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Manage Services' : 'Kelola Layanan'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const itServices = services.filter(s => s.category === 'it');
  const constructionServices = services.filter(s => s.category === 'construction');

  return (
    <AdminLayout title={language === 'en' ? 'Manage Services' : 'Kelola Layanan'}>
      <div className="space-y-8" data-testid="manage-services-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-stone-600">
            {language === 'en' 
              ? 'Add, edit, or remove services displayed on the website.'
              : 'Tambah, edit, atau hapus layanan yang ditampilkan di website.'}
          </p>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Add Service' : 'Tambah Layanan'}
          </Button>
        </div>

        {/* IT Services */}
        <Card data-testid="it-services-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-sky-500" />
              {language === 'en' ? 'IT & Digital Solutions' : 'Solusi IT & Digital'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {itServices.length > 0 ? (
              <div className="space-y-4">
                {itServices.map((service, index) => (
                  <div 
                    key={service.id}
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-lg"
                    data-testid={`it-service-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center text-sky-600">
                        {getIcon(service.icon)}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">
                          {language === 'en' ? service.title_en : service.title_id}
                        </p>
                        <p className="text-sm text-stone-500 line-clamp-1">
                          {language === 'en' ? service.description_en : service.description_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        service.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {service.is_active ? (language === 'en' ? 'Active' : 'Aktif') : (language === 'en' ? 'Inactive' : 'Nonaktif')}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(service)} data-testid={`edit-it-${index}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-it-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-stone-500 py-8">
                {language === 'en' ? 'No IT services yet' : 'Belum ada layanan IT'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Construction Services */}
        <Card data-testid="construction-services-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
              {language === 'en' ? 'Planning & Construction' : 'Perencanaan & Konstruksi'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {constructionServices.length > 0 ? (
              <div className="space-y-4">
                {constructionServices.map((service, index) => (
                  <div 
                    key={service.id}
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-lg"
                    data-testid={`construction-service-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                        {getIcon(service.icon)}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">
                          {language === 'en' ? service.title_en : service.title_id}
                        </p>
                        <p className="text-sm text-stone-500 line-clamp-1">
                          {language === 'en' ? service.description_en : service.description_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        service.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {service.is_active ? (language === 'en' ? 'Active' : 'Aktif') : (language === 'en' ? 'Inactive' : 'Nonaktif')}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(service)} data-testid={`edit-construction-${index}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-construction-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-stone-500 py-8">
                {language === 'en' ? 'No construction services yet' : 'Belum ada layanan konstruksi'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingService 
                ? (language === 'en' ? 'Edit Service' : 'Edit Layanan')
                : (language === 'en' ? 'Add Service' : 'Tambah Layanan')
              }
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'en' ? 'Title (English)' : 'Judul (Inggris)'} *</Label>
                <Input
                  required
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  data-testid="service-title-en"
                />
              </div>
              <div>
                <Label>{language === 'en' ? 'Title (Indonesian)' : 'Judul (Indonesia)'} *</Label>
                <Input
                  required
                  value={formData.title_id}
                  onChange={(e) => setFormData({...formData, title_id: e.target.value})}
                  data-testid="service-title-id"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'en' ? 'Description (English)' : 'Deskripsi (Inggris)'} *</Label>
                <Textarea
                  required
                  rows={3}
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  data-testid="service-desc-en"
                />
              </div>
              <div>
                <Label>{language === 'en' ? 'Description (Indonesian)' : 'Deskripsi (Indonesia)'} *</Label>
                <Textarea
                  required
                  rows={3}
                  value={formData.description_id}
                  onChange={(e) => setFormData({...formData, description_id: e.target.value})}
                  data-testid="service-desc-id"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'en' ? 'Category' : 'Kategori'} *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger data-testid="service-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it">{language === 'en' ? 'IT & Digital' : 'IT & Digital'}</SelectItem>
                    <SelectItem value="construction">{language === 'en' ? 'Construction' : 'Konstruksi'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{language === 'en' ? 'Icon' : 'Ikon'}</Label>
                <Select value={formData.icon} onValueChange={(v) => setFormData({...formData, icon: v})}>
                  <SelectTrigger data-testid="service-icon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'en' ? 'Features (English, comma-separated)' : 'Fitur (Inggris, pisahkan dengan koma)'}</Label>
                <Input
                  value={formData.features_en}
                  onChange={(e) => setFormData({...formData, features_en: e.target.value})}
                  placeholder="Feature 1, Feature 2, Feature 3"
                  data-testid="service-features-en"
                />
              </div>
              <div>
                <Label>{language === 'en' ? 'Features (Indonesian, comma-separated)' : 'Fitur (Indonesia, pisahkan dengan koma)'}</Label>
                <Input
                  value={formData.features_id}
                  onChange={(e) => setFormData({...formData, features_id: e.target.value})}
                  placeholder="Fitur 1, Fitur 2, Fitur 3"
                  data-testid="service-features-id"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="service-active-toggle"
              />
              <Label>{language === 'en' ? 'Active (visible on website)' : 'Aktif (terlihat di website)'}</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {language === 'en' ? 'Cancel' : 'Batal'}
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-service-btn">
                {language === 'en' ? 'Save' : 'Simpan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ManageServices;
